import React, { useState } from "react";
import firebase from "firebase/compat/app";
import DataBase from "../../dataBase/liquidGuides";
import { planMainFix, applyMainFix } from "../../Common/mainScheduleFix";

// Admin Dashboard → Guides: lists the Featured guides whose Main schedule is
// shorter than their longest schedule (or missing) and, on Apply, makes Main
// a copy of the longest one. Check first changes nothing. See
// Common/mainScheduleFix.js. 2026-10-26.
export default function FeaturedMainSchedulePanel() {
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null); // [{ guide, plan, status }]
  const [message, setMessage] = useState("");
  const [confirming, setConfirming] = useState(null); // guides waiting for "Yes, fix"

  const check = () => {
    setBusy(true);
    setMessage("");
    setRows(null);
    DataBase.getFeaturedGuidesData((res) => {
      setBusy(false);
      if (!res.pass) {
        setMessage("Could not read the Featured guides.");
        return;
      }
      const found = res.guides
        .map((guide) => ({ guide, plan: planMainFix(guide.data?.schedule?.events || []), status: "" }))
        .filter((r) => r.plan)
        .sort((a, b) => a.guide.name.localeCompare(b.guide.name));
      setRows(found);
      setMessage(`${res.guides.length} Featured guides checked, ${found.length} to fix.`);
    });
  };

  // Asks on the page, not with window.confirm: embedded browsers (e.g. the
  // VS Code preview) block confirm() silently, so Apply seemed to do nothing.
  const apply = () => {
    const uid = firebase.auth().currentUser?.uid;
    const mine = rows.filter((r) => r.guide.owner === uid && r.status !== "fixed");
    if (mine.length === 0) {
      setMessage("None of these guides belong to the signed-in account, so they can't be changed from here.");
      return;
    }
    setMessage("");
    setConfirming(mine);
  };

  const applyConfirmed = async () => {
    const mine = confirming;
    setConfirming(null);
    setBusy(true);
    let done = 0;
    let failed = 0;
    for (const r of mine) {
      let res;
      try {
        const data = { ...r.guide.data, schedule: { ...r.guide.data.schedule, events: applyMainFix(r.guide.data.schedule.events, r.plan) } };
        // eslint-disable-next-line no-await-in-loop
        res = await new Promise((resolve) => DataBase.replaceGuideDataContent(r.guide.guide_id, data, resolve));
      } catch (err) {
        console.error("Main schedule fix failed", r.guide.guide_id, err);
        res = { pass: false };
      }
      r.status = res && res.pass ? "fixed" : "failed";
      if (res && res.pass) done++;
      else failed++;
      setRows([...rows]);
    }
    setBusy(false);
    const others = rows.length - mine.length;
    setMessage(`${done} fixed${failed ? `, ${failed} failed` : ""}${others ? `, ${others} owned by another account (skipped)` : ""}.`);
  };

  return (
    <div className="admindash-lookup">
      <p className="tai-paste-title admindash-lookup-title">Featured guides: Main schedule</p>
      <p className="tai-paste-hint">
        Finds Featured guides whose Main schedule (the one a guide opens on) is shorter than their longest schedule, or
        missing. Apply makes Main a copy of the longest schedule; the other schedules stay as they are.
      </p>
      <div className="admindash-lookup-row">
        <button className="tai-cancel-btn" onClick={check} disabled={busy}>
          {busy && !rows ? "Checking…" : "Check"}
        </button>
        {rows && rows.length > 0 && !confirming && (
          <button className="tai-primary-btn" onClick={apply} disabled={busy}>
            {busy ? "Applying…" : `Apply to ${rows.filter((r) => r.status !== "fixed").length}`}
          </button>
        )}
        {confirming && (
          <>
            <button className="tai-primary-btn" onClick={applyConfirmed}>
              Yes, make Main the longest schedule in {confirming.length} guides
            </button>
            <button className="tai-cancel-btn" onClick={() => setConfirming(null)}>
              Cancel
            </button>
          </>
        )}
      </div>
      {message !== "" && <p className="tai-paste-hint">{message}</p>}
      {rows && rows.length > 0 && (
        <div style={{ maxHeight: 260, overflowY: "auto", fontSize: 13 }}>
          {rows.map((r) => (
            <div key={r.guide.guide_id}>
              {r.guide.name}: Main {r.plan.mainDays === 0 ? "missing" : `${r.plan.mainDays} days`} → {r.plan.fromDays} days
              (from “{r.plan.from}”){r.status ? ` — ${r.status}` : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
